import React, { useState } from "react";
import { FaArrowLeft, FaArrowRight, FaX, FaExpand } from "react-icons/fa6";

interface ImageGalleryProps {
  images: string[];
  title?: string;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!images || images.length === 0) {
    return <div className="text-gray-5 italic">No images available</div>;
  }

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-sm font-medium mb-2 text-gray-7">{title}</h3>
      )}

      <div className="relative bg-gray-1 rounded-lg overflow-hidden">
        {/* Thumbnail Grid */}
        <div className="grid grid-cols-4 gap-2 p-3">
          {images.slice(0, 4).map((image, index) => (
            <div
              key={index}
              className={`aspect-square cursor-pointer relative rounded-md overflow-hidden ${
                index === currentIndex ? "ring-2 ring-gray-7" : ""
              }`}
              onClick={() => setCurrentIndex(index)}
            >
              <img
                src={image}
                alt={`Evidence ${index + 1}`}
                className="w-full h-full object-cover"
              />
              {index === 3 && images.length > 4 && (
                <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center text-white">
                  +{images.length - 4}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* View Full Button */}
        <button
          onClick={openModal}
          className="absolute top-3 right-3 p-2 rounded-full bg-white bg-opacity-80 text-gray-7 hover:bg-opacity-100"
        >
          <FaExpand size={12} />
        </button>
      </div>

      {/* Lightbox Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
          <div className="relative w-full max-w-4xl max-h-dvh p-4">
            <button
              onClick={closeModal}
              className="absolute top-0 right-0 z-10 p-2 text-white bg-black bg-opacity-50 rounded-full"
            >
              <FaX size={18} />
            </button>

            <div className="relative">
              <img
                src={images[currentIndex]}
                alt={`Evidence ${currentIndex + 1}`}
                className="max-h-[80vh] max-w-full mx-auto"
              />

              <button
                onClick={handlePrevious}
                className="absolute left-0 top-1/2 transform -translate-y-1/2 p-2 bg-black bg-opacity-50 text-white rounded-r-lg"
              >
                <FaArrowLeft size={18} />
              </button>

              <button
                onClick={handleNext}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 p-2 bg-black bg-opacity-50 text-white rounded-l-lg"
              >
                <FaArrowRight size={18} />
              </button>
            </div>

            <p className="text-white text-center mt-2">
              {currentIndex + 1} / {images.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
